/**
 * Two-way binding between two axes that show the same quantity.
 *
 * /graph draws time up the vertical axis of both charts over the same cure, so
 * any moment the two ranges disagree is a moment one of them is lying. Desktop
 * shares the RANGES, not merely the scrub tick, and this is that. It is also what
 * makes hiding one of the two axes safe: a hidden axis free to drift would
 * silently mislabel the chart it belongs to.
 */
export interface RangeLike {
  readonly min: number;
  readonly max: number;
}

export interface AxisLike {
  visibleRange: RangeLike;
}

/**
 * THE GUARD IS THE WHOLE POINT. SciChart raises visibleRangeChanged for
 * PROGRAMMATIC assignments as well as user gestures, so a naive two-way binding
 * has each side answering the other. The flag is held across the assignment, and
 * released in a finally so a throw cannot wedge the binding shut permanently.
 *
 * One sharer instance must serve BOTH directions — that shared closure variable
 * is what suppresses the echo. Two independent sharers would each see the other's
 * write as a fresh user change.
 */
export function createRangeSharer<R extends RangeLike>(
  makeRange: (min: number, max: number) => R
): (from: AxisLike, to: AxisLike) => void {
  let syncing = false;

  return (from: AxisLike, to: AxisLike) => {
    if (syncing) return;
    syncing = true;
    try {
      const r = from.visibleRange;
      // Equal ranges are left alone. Assigning an identical range still raises
      // the event, and on a chart that is already synchronised that is pure work
      // for no change — on every frame of a drag.
      if (to.visibleRange.min !== r.min || to.visibleRange.max !== r.max) {
        to.visibleRange = makeRange(r.min, r.max);
      }
    } finally {
      syncing = false;
    }
  };
}
